import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import {
    buildActivePrinterCards,
    removeInProgressJob,
    upsertInProgressJob,
} from '../../helpers/activePrinters';
import { fetchJobDetails } from '../../helpers/jobLivePatch';
import { seedJobLiveInProgress } from '../../helpers/jobLiveInProgress';
import {
    isJobIntegrationLiveNotification,
    LIVE_NOTIFICATION_KINDS,
    subscribeLiveNotifications,
} from '../../helpers/liveNotifications';
import {
    clearPrinterPrintProgress,
    loadPrinterPrintProgressMap,
} from '../../helpers/printerPrintProgressStorage';
import { filterActivePrinters } from '../../helpers/printers';
import ActivePrinterCard from './ActivePrinterCard';

export default function ActivePrintersPanel() {
    const navigate = useNavigate();
    const [inProgressJobs, setInProgressJobs] = useState([]);
    const [printersById, setPrintersById] = useState({});
    const [printerProgress, setPrinterProgress] = useState({});
    const [loading, setLoading] = useState(true);

    const loadActivePrinters = useCallback(() => (
        Promise.all([
            api.get('jobs?status=em_andamento&limit=200'),
            api.get('printers?page=1&limit=200'),
        ])
            .then(([jobsRes, printersRes]) => {
                const jobs = jobsRes.data.data?.jobs || [];
                const printers = filterActivePrinters(printersRes.data.printers || []);
                const byId = {};
                printers.forEach((printer) => {
                    byId[printer.id] = printer;
                });

                setPrintersById(byId);
                setInProgressJobs(jobs);
                seedJobLiveInProgress(jobs);

                const printerUuids = [...new Set(jobs.map((job) => job.printer_uuid).filter(Boolean))];
                setPrinterProgress((prev) => ({
                    ...loadPrinterPrintProgressMap(printerUuids),
                    ...prev,
                }));
            })
            .catch(() => {
                setInProgressJobs([]);
            })
    ), []);

    useEffect(() => {
        let active = true;

        loadActivePrinters()
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [loadActivePrinters]);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (!isJobIntegrationLiveNotification(detail)) {
                return;
            }

            if (detail.action === 'finished') {
                clearPrinterPrintProgress(detail.printerUuid);
                setPrinterProgress((prev) => {
                    if (!prev[detail.printerUuid]) {
                        return prev;
                    }
                    const next = { ...prev };
                    delete next[detail.printerUuid];
                    return next;
                });
                setInProgressJobs((prev) => removeInProgressJob(prev, detail.jobUuid));
                return;
            }

            if (detail.action === 'started') {
                fetchJobDetails(detail.jobUuid).then((job) => {
                    if (!job || job.status !== 'em_andamento') {
                        return;
                    }
                    setInProgressJobs((prev) => upsertInProgressJob(prev, job));
                });
            }
        });
    }, []);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (detail?.kind !== LIVE_NOTIFICATION_KINDS.PRINTER_SNAPSHOT_UPDATED) {
                return;
            }
            if (detail.progressPercent == null || !detail.printerUuid) {
                return;
            }
            setPrinterProgress((prev) => ({
                ...prev,
                [detail.printerUuid]: detail.progressPercent,
            }));
        });
    }, []);

    const cards = useMemo(
        () => buildActivePrinterCards(inProgressJobs, printersById),
        [inProgressJobs, printersById],
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, mb: 2 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (cards.length === 0) {
        return null;
    }

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                Impressoras em uso no momento
            </Typography>
            <Grid container spacing={2}>
                {cards.map((card) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={card.printer_uuid}>
                        <ActivePrinterCard
                            card={card}
                            progressMap={printerProgress}
                            onClick={() => navigate('/admin/jobs')}
                            onFilamentsChanged={(printerUuid, filaments) => {
                                setPrintersById((prev) => {
                                    const current = prev[printerUuid] || {};
                                    return {
                                        ...prev,
                                        [printerUuid]: { ...current, filaments },
                                    };
                                });
                            }}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
